/**
 * Models write LaTeX with whatever delimiters they were trained on (WF-2767):
 *
 * - OpenAI models use `\[ ... \]` and `\( ... \)`, which Markdown reads as
 *   escaped brackets/parens, so the TeX leaks through as plain text.
 * - Gemini, Llama, Mistral and often Claude use `$ ... $` for inline math.
 * - Some models emit bare `\begin{align} ... \end{align}` with no delimiters.
 *
 * `remark-math` only understands `$$` (we keep its single-dollar syntax off
 * because it turns prices into math), so this rewrites every form above into
 * `$$` before the Markdown is parsed. Code spans, fenced code and existing
 * `$$` math are left untouched.
 */

const DISPLAY_ENVIRONMENTS = 'equation|align|alignat|gather|multline|flalign|eqnarray|displaymath'

const TOKEN = new RegExp(
  [
    // Fenced code block, possibly still unterminated while streaming.
    String.raw`(?<fence>(?<=^|\n)[ \t]{0,3}(?<fenceMark>\`{3,}|~{3,})[^\n]*(?:\n[\s\S]*?)?(?:\n[ \t]{0,3}\k<fenceMark>[ \t]*(?=\n|$)|$))`,
    // Inline code span.
    String.raw`(?<codeSpan>(?<ticks>\`+)[\s\S]*?[^\`]\k<ticks>(?!\`))`,
    // Math that is already `$$`-delimited.
    String.raw`(?<dollarMath>\$\$(?<dollarInner>[\s\S]+?)\$\$)`,
    // \[ display \]
    String.raw`(?<bracketMath>\\\[(?<bracketInner>[\s\S]+?)\\\])`,
    // \( inline \)
    String.raw`(?<parenMath>\\\((?<parenInner>[\s\S]+?)\\\))`,
    // \begin{align} ... \end{align}
    String.raw`(?<envMath>\\begin\{(?<env>${DISPLAY_ENVIRONMENTS})\*?\}[\s\S]*?\\end\{\k<env>\*?\})`,
    // $inline$, using Pandoc's rules so prices like "$5 and $10" stay text:
    // the opener isn't followed by whitespace, the closer isn't preceded by
    // whitespace or followed by a digit, and neither is backslash-escaped.
    String.raw`(?<singleMath>(?<![\\$])\$(?![\s$])(?<singleInner>(?:[^$\n\\]|\\.)+?)(?<![\s\\])\$(?![\d$]))`,
  ].join('|'),
  'g'
)

/** Whitespace-only prefix of the line `offset` sits on, or `null` if other text precedes it. */
const lineIndentBefore = (text: string, offset: number): string | null => {
  const lineStart = text.lastIndexOf('\n', offset - 1) + 1
  const prefix = text.slice(lineStart, offset)
  return /^[ \t]*$/.test(prefix) ? prefix : null
}

const endsLine = (text: string, end: number): boolean => /^[ \t]*(?:\n|$)/.test(text.slice(end))

/** Inline math can't contain a blank line, since that would end the paragraph. */
const inline = (tex: string): string => `$$${tex.trim().replace(/\n[ \t]*\n/g, '\n')}$$`

/**
 * Display math becomes a `$$` fence when it has a line to itself (keeping the
 * line's indentation so it stays inside list items), otherwise inline math.
 */
const display = (tex: string, text: string, offset: number, length: number): string => {
  const indent = lineIndentBefore(text, offset)
  if (indent === null || !endsLine(text, offset + length)) {
    return inline(tex)
  }
  const body = tex
    .trim()
    .split('\n')
    .map(line => indent + line.trim())
    .join('\n')
  // The indent before the match is still in the output, so the opener doesn't repeat it.
  return `$$\n${body}\n${indent}$$`
}

export const normalizeMathDelimiters = (markdown: string): string => {
  if (!/[$\\]/.test(markdown)) {
    return markdown
  }

  return markdown.replace(TOKEN, (match: string, ...args: unknown[]) => {
    const groups = args[args.length - 1] as Record<string, string | undefined>
    const offset = args[args.length - 3] as number

    if (groups.fence !== undefined || groups.codeSpan !== undefined) {
      return match
    }
    if (groups.dollarInner !== undefined) {
      // A lone `$$x$$` line is meant as display math, but remark-math only
      // treats `$$` as a block when the fences are on their own lines.
      return groups.dollarInner.includes('\n') ? match : display(groups.dollarInner, markdown, offset, match.length)
    }
    if (groups.bracketInner !== undefined) {
      return display(groups.bracketInner, markdown, offset, match.length)
    }
    if (groups.envMath !== undefined) {
      return display(groups.envMath, markdown, offset, match.length)
    }
    if (groups.parenInner !== undefined) {
      return inline(groups.parenInner)
    }
    if (groups.singleInner !== undefined) {
      return inline(groups.singleInner)
    }
    return match
  })
}
